import React, { useCallback, useEffect, useRef, useState } from 'react'
import api from '../../../../../services/api'
import Form, { Input, Select, Textarea } from '../../../../../components/Form'
import { useFormContext, UseFormReturn } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useToast } from '../../../../../hooks/toast'
import { useLoading } from '../../../../../hooks/loading'
import { useUpdateDataTable } from '../../../../../hooks/dataTable'
import { apiCreate, apiUpdate } from '../../domain/api'
import { nameActions } from '../../domain/info'
import { FieldError, FormContainer } from './styles'
import { getFileNameWithOutHash } from '../../../../../utils/getFileNameWithOutHash'
import { Upload } from '../Upload'

type TypesFormProps = {
  initialValues?: AdvertisementsApiResponse & {
    idUpdate: number
  }
  typeForm: 'create' | 'update'
}

const toastSuccessData: Record<string, () => ToastMessage> = {
  create: () => ({
    type: 'success',
    title: 'Registro criado',
    description: 'Registro criado com sucesso'
  }),
  update: () => ({
    type: 'success',
    title: 'Registro atualizado',
    description: 'Registro alterado com sucesso'
  })
}

const toastErrorData: Record<string, () => ToastMessage> = {
  create: () => ({
    type: 'error',
    title: 'Erro ao adicionar o registro',
    description:
      'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
  }),
  update: () => ({
    type: 'error',
    title: 'Erro ao atualizar',
    description:
      'Ocorreu um erro ao alterar os dados, por favor, tente novamente.'
  })
}

const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png']

const isAllowedAdvertisementImage = (file: File): boolean => {
  if (ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) {
    return true
  }

  return /\.(jpe?g|png)$/i.test(file.name)
}

const isValidImageField = (image?: string | null): boolean => {
  return Boolean(image && image !== 'null')
}

const isValidUrl = (value?: string | null): boolean => {
  if (!value?.trim()) {
    return true
  }

  try {
    const url = new URL(value.trim())
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

const hasImage = (
  imageFile: AdvertisementFile | undefined,
  imageRemoved: boolean
): boolean => {
  return Boolean(
    imageFile && !imageRemoved && imageFile.file && imageFile.file.name
  )
}

const FieldErrorMessage = ({ name }: { name: string }): JSX.Element | null => {
  const {
    formState: { errors }
  } = useFormContext()
  const error = errors[name]

  if (!error?.message) {
    return null
  }

  return <FieldError>{String(error.message)}</FieldError>
}

const validateAdvertisementForm = ({
  data,
  imageFile,
  imageRemoved,
  setError,
  clearErrors,
  setImageError
}: {
  data: any
  imageFile?: AdvertisementFile
  imageRemoved: boolean
  setError: UseFormReturn['setError']
  clearErrors: UseFormReturn['clearErrors']
  setImageError: (message?: string) => void
}): boolean => {
  clearErrors(['title', 'description', 'link'])
  setImageError(undefined)

  const title = data.title?.trim()
  const description = data.description?.trim()
  const link = data.link?.trim()
  const imagePresent = hasImage(imageFile, imageRemoved)
  let isValid = true

  if (!title) {
    setError('title', {
      type: 'required',
      message: 'Título é obrigatório'
    })
    isValid = false
  }

  if (link && !isValidUrl(link)) {
    setError('link', {
      type: 'validate',
      message: 'Informe uma URL válida (ex: https://exemplo.com)'
    })
    isValid = false
  }

  const hasTextCombo = Boolean(title && description && link && isValidUrl(link))
  const hasImageCombo = Boolean(title && imagePresent)

  if (!hasTextCombo && !hasImageCombo) {
    if (!description) {
      setError('description', {
        type: 'required',
        message: imagePresent
          ? 'Descrição é obrigatória'
          : 'Descrição é obrigatória (ou adicione uma imagem)'
      })
      isValid = false
    }

    if (!link) {
      setError('link', {
        type: 'required',
        message: imagePresent
          ? 'Link é obrigatório'
          : 'Link é obrigatório (ou adicione uma imagem)'
      })
      isValid = false
    }

    if (!imagePresent) {
      setImageError(
        'Adicione uma imagem ou preencha descrição e link (URL válida)'
      )
      isValid = false
    }
  }

  return isValid
}

const FormMethodsBridge = ({
  formMethodsRef
}: {
  formMethodsRef: React.MutableRefObject<UseFormReturn | null>
}): null => {
  const methods = useFormContext()

  useEffect(() => {
    formMethodsRef.current = methods
  }, [formMethodsRef, methods])

  return null
}

const ClearFieldErrorsOnChange = (): null => {
  const { watch, clearErrors } = useFormContext()
  const description = watch('description')
  const link = watch('link')

  useEffect(() => {
    if (description) {
      clearErrors('description')
    }
  }, [clearErrors, description])

  useEffect(() => {
    if (link) {
      clearErrors('link')
    }
  }, [clearErrors, link])

  return null
}

export const FormAdvertisements = ({
  initialValues,
  typeForm
}: TypesFormProps): JSX.Element => {
  const { addToast } = useToast()
  const history = useHistory()
  const { updateDataTable } = useUpdateDataTable()
  const { activeLoading, disableLoading } = useLoading()
  const formMethodsRef = useRef<UseFormReturn | null>(null)
  const [imageFile, setImageFile] = useState<AdvertisementFile>()
  const [imageRemoved, setImageRemoved] = useState(false)
  const [imageError, setImageError] = useState<string>()

  const onSubmitForm = useCallback(
    async (data: any) => {
      const formMethods = formMethodsRef.current

      if (
        !formMethods ||
        !validateAdvertisementForm({
          data,
          imageFile,
          imageRemoved,
          setError: formMethods.setError,
          clearErrors: formMethods.clearErrors,
          setImageError
        })
      ) {
        return
      }

      const formData = new FormData()

      Object.entries(data).forEach(([key, value]: [string, any]) => {
        if (key === 'image') {
          return
        }
        if (typeForm === 'create' && key === 'active') {
          return
        }
        if (value === undefined || value === null || value === '') {
          if (typeForm === 'update' && key === 'link') {
            formData.append('link', '')
          }
          return
        }
        formData.append(key, value)
      })

      if (imageFile?.isNew) {
        if (imageFile.file) {
          formData.append('image', imageFile.file)
        } else if (typeForm === 'update') {
          formData.append('image', 'null')
        }
      }

      if (typeForm === 'update' && imageRemoved) {
        formData.append('remove_image', '1')
      }

      const id = initialValues?.idUpdate
      const apiRequest =
        typeForm === 'create'
          ? api.post(apiCreate(), formData)
          : api.put(apiUpdate(id.toString()), formData)

      try {
        activeLoading()
        await apiRequest
        addToast(toastSuccessData[typeForm]?.())
        if (typeForm === 'update') {
          updateDataTable()
        }
        history.push(nameActions.read.to)
      } catch (error) {
        addToast(toastErrorData[typeForm]?.())
      } finally {
        disableLoading()
      }
    },
    [
      activeLoading,
      addToast,
      disableLoading,
      history,
      imageFile,
      imageRemoved,
      initialValues?.idUpdate,
      typeForm,
      updateDataTable
    ]
  )

  useEffect(() => {
    if (!initialValues?.image_url || !isValidImageField(initialValues.image)) {
      return
    }

    fetch(initialValues.image_url)
      .then(response => response.blob())
      .then(blob => {
        const fileName =
          getFileNameWithOutHash(initialValues.image || '') ||
          `anuncio-${initialValues.id}.jpg`
        const file = new File([blob], fileName)
        setImageFile({ isNew: false, file })
      })
      .catch(error => {
        console.error('Erro ao baixar o arquivo:', error)
      })
  }, [initialValues?.id, initialValues?.image, initialValues?.image_url])

  const handleImageChange = useCallback((file: File) => {
    if (!isAllowedAdvertisementImage(file)) {
      setImageError('Apenas imagens JPEG e PNG são permitidas')
      return
    }

    setImageFile({ file, isNew: true })
    setImageRemoved(false)
    setImageError(undefined)
    formMethodsRef.current?.clearErrors(['description', 'link'])
  }, [])

  const handleImageRemove = useCallback(() => {
    setImageFile({ isNew: true, file: null as unknown as File })
    setImageRemoved(true)
  }, [])

  return (
    <Form
      onSubmit={onSubmitForm}
      defaultValues={{
        title: initialValues?.title,
        description: initialValues?.description,
        link: initialValues?.link,
        ...(typeForm === 'update' && {
          active: Number(initialValues?.active)
        })
      }}
    >
      <FormMethodsBridge formMethodsRef={formMethodsRef} />
      <ClearFieldErrorsOnChange />
      <div className="card">
        <FormContainer>
          <div className="row mb-5">
            <div className="col-md-12">
              <span className="text-danger fs-7">
                Preencha título, descrição e link (URL válida), ou título com
                imagem.
              </span>
            </div>
            <div className="col-md-6">
              <Input
                name="title"
                label="Título *"
                rules={{
                  required: 'Título é obrigatório',
                  maxLength: 150
                }}
              />
              <FieldErrorMessage name="title" />
            </div>
            <div className="col-md-6">
              <Input
                name="link"
                label="Link (URL válida)"
                placeholder="https://exemplo.com"
                rules={{
                  maxLength: 255,
                  validate: value =>
                    isValidUrl(value) ||
                    'Informe uma URL válida (ex: https://exemplo.com)'
                }}
              />
              <FieldErrorMessage name="link" />
            </div>
            <Textarea
              name="description"
              label="Descrição"
              className="col-md-12"
            />
            <div className="col-md-12">
              <FieldErrorMessage name="description" />
            </div>
            {typeForm === 'update' && (
              <Select
                className="col-md-2"
                name="active"
                label="Ativo"
                options={[
                  {
                    name: 'Sim',
                    value: 1
                  },
                  {
                    name: 'Não',
                    value: 0
                  }
                ]}
                rules={{ required: true }}
                blank
              />
            )}
            <div className="col-md-6">
              <label className="col-form-label fw-bold fs-6">Imagem</label>
              <input
                type="file"
                accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                className={`form-control ${imageError ? 'is-invalid' : ''}`}
                onChange={event => {
                  const file = event.target.files?.[0]
                  if (!file) {
                    return
                  }

                  handleImageChange(file)
                  event.target.value = ''
                }}
              />
              {imageError && <FieldError>{imageError}</FieldError>}
              {hasImage(imageFile, imageRemoved) && imageFile && (
                <div className="mt-4">
                  <Upload
                    file={imageFile.file}
                    handleRemoveFile={() => handleImageRemove()}
                  />
                </div>
              )}
            </div>
          </div>
        </FormContainer>
        <div className="card-footer d-flex justify-content-end py-6 px-9">
          <button type="submit" className="btn btn-primary">
            Salvar
          </button>
        </div>
      </div>
    </Form>
  )
}
